import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radar, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Register() {
  const { register, status } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (status === "authed") navigate("/globe", { replace: true });
  }, [status, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await register(email.trim(), password, name.trim());
      navigate("/globe", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen speculo-grid-bg flex items-center justify-center px-4 py-10"
      data-testid="register-page"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-sm border border-cyan-400/60 flex items-center justify-center speculo-glow">
            <Radar className="w-5 h-5 text-cyan-300" />
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold tracking-[0.28em] text-cyan-200">SPECULO</div>
            <div className="text-[10px] text-cyan-500/70 tracking-[0.4em] uppercase font-mono">
              Global Insight
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="speculo-glass rounded-sm p-8"
          data-testid="register-form"
        >
          <div className="flex items-center gap-2 mb-1 text-cyan-300">
            <UserPlus className="w-4 h-4" />
            <h1 className="text-sm uppercase tracking-widest font-semibold">Provision Workspace</h1>
          </div>
          <p className="text-xs text-slate-400 mb-6">
            A workspace will be minted the moment credentials are forged.
          </p>

          <label className="block text-[11px] uppercase tracking-widest text-cyan-500/80 mb-1">
            Operator Name
          </label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full mb-4 bg-[#04121a] border border-cyan-900/60 focus:border-cyan-400/70 rounded-sm px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-all"
            data-testid="register-name-input"
            placeholder="e.g. Aria Vale"
          />

          <label className="block text-[11px] uppercase tracking-widest text-cyan-500/80 mb-1">
            Email
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-4 bg-[#04121a] border border-cyan-900/60 focus:border-cyan-400/70 rounded-sm px-3 py-2 font-mono text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-all"
            data-testid="register-email-input"
            placeholder="you@example.com"
          />

          <label className="block text-[11px] uppercase tracking-widest text-cyan-500/80 mb-1">
            Passphrase (min 6)
          </label>
          <input
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-6 bg-[#04121a] border border-cyan-900/60 focus:border-cyan-400/70 rounded-sm px-3 py-2 font-mono text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-all"
            data-testid="register-password-input"
            placeholder="••••••••"
          />

          {error && (
            <div
              className="mb-4 text-xs text-[#ff4d70] border border-[#FF003C]/40 bg-[#FF003C]/10 rounded-sm px-3 py-2 font-mono"
              data-testid="register-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 py-3 bg-cyan-400 text-[#040d14] font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-cyan-300 hover:shadow-[0_0_18px_rgba(0,229,255,0.5)] transition-all disabled:opacity-50"
            data-testid="register-submit-button"
          >
            <UserPlus className="w-4 h-4" />
            {submitting ? "Provisioning…" : "Create Workspace"}
          </button>

          <div className="mt-6 flex items-center justify-between text-[11px] text-slate-400">
            <span className="uppercase tracking-widest">Already provisioned?</span>
            <Link
              to="/login"
              className="text-cyan-300 hover:text-cyan-200 uppercase tracking-widest font-semibold"
              data-testid="register-goto-login"
            >
              ← Return to login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
